/**
 * Session and tenancy context.
 *
 * The server is the authority on role — this only mirrors what /me returned,
 * so hiding a nav item is convenience, never security. Every guarded action
 * is refused by the API regardless of what the UI shows.
 *
 * Clerk is loaded lazily: the app must still run in development with
 * AUTH_DEV_BYPASS, and the public tracker/quote pages must not pull in an
 * auth SDK at all.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, configureAuth, hasCredential, setActiveGarage } from "./api";

export type Role = "admin" | "supervisor" | "technician" | "helper";

export interface Membership {
  garage_id: string;
  /** Your own membership id — what assignments and messages are keyed by. */
  member_id: string;
  garage_name: string;
  garage_slug: string;
  role: Role;
  can_authorize_qa: boolean;
  setup_completed: boolean;
  billing_mode: "subscription" | "licensed" | "trial";
  status: "pending" | "active" | "suspended" | "cancelled";
}

export interface Session {
  user_id: string;
  email: string;
  full_name: string | null;
  is_superadmin: boolean;
  garages: Membership[];
}

interface AuthValue {
  session: Session | null;
  loading: boolean;
  error: string | null;
  garage: Membership | null;
  role: Role | null;
  switchGarage: (garageId: string) => void;
  refresh: () => Promise<void>;
  /** admin > supervisor > technician. Helper is NOT on this ladder. */
  atLeast: (role: Exclude<Role, "helper">) => boolean;
  is: (role: Role) => boolean;
  canAuthorizeQa: boolean;
  /** Your membership id in the active garage. */
  memberId: string | null;
}

const RANK: Record<string, number> = { technician: 1, supervisor: 2, admin: 3 };

const AuthContext = createContext<AuthValue | null>(null);

const GARAGE_KEY = "garageos.activeGarage";

/**
 * Whether `ClerkRoot`'s `ClerkTokenBridge` will exist at all for this build.
 *
 * When it will, it is the one that eventually calls `configureAuth` with a
 * real, Clerk-aware getter and then `refresh()` — but it lives inside a lazy
 * chunk, mounted deeper in the router tree, so it only fires once Clerk has
 * loaded. If `AuthProvider` also fires its own `refresh()` on mount, that
 * call runs first, sees the default token getter (`() => null`, nobody has
 * configured it yet), concludes "nobody's signed in", and renders the
 * landing page — which then gets yanked away for the real dashboard a moment
 * later once Clerk actually reports in. That flash is exactly what a hard
 * reload used to show. When Clerk is going to drive this anyway, leave
 * `loading` true and let it.
 */
const CLERK_WILL_DRIVE_AUTH = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);

export function AuthProvider({
  children,
  devToken,
}: {
  children: ReactNode;
  devToken?: string;
}) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [garageId, setGarageId] = useState<string | null>(
    () => localStorage.getItem(GARAGE_KEY),
  );

  useEffect(() => {
    configureAuth(() => devToken ?? localStorage.getItem("garageos.token"));
  }, [devToken]);

  const refresh = useCallback(async () => {
    // No credential, no round-trip. AuthProvider wraps the whole tree, so
    // without this a customer opening a tracker link would fire an
    // authenticated /me at the API purely to be told 401 — a wasted request
    // on mobile data, and a misleading error in their console.
    //
    // This asks the SAME getter `configureAuth` points requests at, rather
    // than peeking at `localStorage` directly. A direct peek here silently
    // stopped agreeing with reality the moment ClerkTokenBridge started
    // pointing that getter at a live Clerk session instead — every real
    // Clerk sign-in short-circuited to "nobody's signed in" right here,
    // without /me ever being called, however correctly the token bridge
    // itself was wired.
    if (!(await hasCredential())) {
      setSession(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const me = await api.get<Session>("/me");
      setSession(me);

      // Pick a garage if the stored one is gone or none is chosen.
      const stored = localStorage.getItem(GARAGE_KEY);
      const valid = me.garages.find((g) => g.garage_id === stored);
      const next = valid?.garage_id ?? me.garages[0]?.garage_id ?? null;

      setGarageId(next);
      setActiveGarage(next);
      if (next) localStorage.setItem(GARAGE_KEY, next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load your account");
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [devToken]);

  useEffect(() => {
    // ClerkTokenBridge performs the first meaningful refresh() itself, once
    // Clerk has actually loaded and can say whether anyone is signed in.
    // Running our own here first would only ever see "no credential yet".
    if (CLERK_WILL_DRIVE_AUTH) return;
    void refresh();
  }, [refresh]);

  const switchGarage = useCallback((id: string) => {
    setGarageId(id);
    setActiveGarage(id);
    localStorage.setItem(GARAGE_KEY, id);
  }, []);

  const value = useMemo<AuthValue>(() => {
    const garage = session?.garages.find((g) => g.garage_id === garageId) ?? null;
    const role = garage?.role ?? null;

    return {
      session,
      loading,
      error,
      garage,
      role,
      switchGarage,
      refresh,
      atLeast: (target) => {
        if (session?.is_superadmin) return true;
        // Helper never satisfies a rank check — it is a different access
        // path, not a lower admin.
        if (!role || role === "helper") return false;
        return (RANK[role] ?? 0) >= RANK[target];
      },
      is: (target) => role === target,
      canAuthorizeQa: Boolean(garage?.can_authorize_qa),
      memberId: garage?.member_id ?? null,
    };
  }, [session, loading, error, garageId, switchGarage, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
